import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ModalTransactionParkingDetailComponent } from '../../../modals/parking/transaction-parking/modal-transaction-parking-detail/modal-transaction-parking-detail.component';
import { ModalTransactionParkingEditLicenseComponent } from '../../../modals/parking/transaction-parking/modal-transaction-parking-edit-license/modal-transaction-parking-edit-license.component';
import { ModalForceEnterTransactionComponent } from '../../../modals/parking/transaction-parking/modal-force-enter-transaction/modal-force-enter-transaction.component';
import { ModalForceExitTransactionComponent } from '../../../modals/parking/transaction-parking/modal-force-exit-transaction/modal-force-exit-transaction.component';
import { EditTransactionLicenseData, ParkingGateListResponse, ParkingTransactionResponse } from '../../../../../core/models/parking.model';
import { ApiService } from '../../../../../core/services/api.service';
import { ParkingTransactionDashboardStats } from '../../../../../core/models/parking/transaction-parking.model';
import { ApiTransactionParkingService } from '../../../../../core/services/api/parking/api-transaction-parking.service';
import { ApiFormService } from '../../../../../core/services/api-form.service';
import { MapperTransactionParkingService } from '../../../../../core/mappers/parking/mapper-transaction-parking.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';

@Component({
  selector: 'app-transaction-parking',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalTransactionParkingDetailComponent,
    ModalTransactionParkingEditLicenseComponent,
    ModalForceEnterTransactionComponent,
    ModalForceExitTransactionComponent,
  ],
  templateUrl: './transaction-parking.component.html',
  styleUrls: ['./transaction-parking.component.css']
})
export class TransactionParkingComponent implements OnInit, OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly apiService = inject(ApiTransactionParkingService);
  private readonly apiFormService = inject(ApiFormService);
  private readonly mapper = inject(MapperTransactionParkingService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly legacyApiService = inject(ApiService);
  private readonly destroy$ = new Subject<void>();

  // --- Modal State ---
  showDetailModal = false;
  selectedParkingTransaction: ParkingTransactionResponse | null = null;

  showEditLicenseModal = false;
  editLicenseData: EditTransactionLicenseData = { userId: 0, oldLicense: '', transactionId: 0, entryPath1: '' };

  showForceEnterModal = false;
  showForceExitModal = false;
  selectedForceExitTransaction: ParkingTransactionResponse | null = null;

  selectedTransactions = new Set<number>();
  showBulkCloseModal = false;
  bulkRemark = '';
  isBulkClosing = false;

  showImageModal = false;
  selectedImageUrl = '';

  // --- Search & Filter ---
  startDate = '';
  endDate = '';
  searchText = '';
  searchType: 'license_plate' | 'site_name' | 'card_name' | 'gate_name' = 'license_plate';
  isSearchTypeDropdownOpen = false;

  selectedGates: Record<string, boolean> = {};
  gateOptions: string[] = [];
  gateSearchText = '';

  selectedVehicleTypes: Record<string, boolean> = {};
  vehicleTypeOptions: string[] = [];

  statusFilter: 'all' | 'active' | 'completed' = 'all';

  // --- Data ---
  transactions: ParkingTransactionResponse[] = [];
  isLoading = false;
  userId = 0;

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  dashboardStats: ParkingTransactionDashboardStats = {
    totalTransactions: 0,
    activeCount: 0,
    completedCount: 0,
    longestParkingList: [],
  };

  showDashboard = true;
  showMobileFilter = false;
  collapsedFilters: Record<string, boolean> = {};

  enableLiveRefresh = false;
  private liveRefreshInterval: ReturnType<typeof setInterval> | null = null;
  lastFetchedTime: Date | null = null;

  // --- Chart ---
  dashboardData: ParkingTransactionResponse[] = [];
  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  customStartDate = '';
  customEndDate = '';
  chartStartDate = '';
  chartEndDate = '';
  chartType: 'line' | 'bar' = 'line';
  selectedMonthStart = new Date().toISOString().slice(0, 7);
  selectedMonthEnd = new Date().toISOString().slice(0, 7);
  yAxisLabels: number[] = [];
  currentMonthPoints = '';
  currentMonthAreaPoints = '';
  currentMonthDots: any[] = [];
  chartTotalCount = 0;
  recentActivity: any[] = [];

  sortColumn = 'entryDate';
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor() {
    const today = new Date();
    this.startDate = today.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;

      untracked(() => {
        this.currentPage = 1;
        this.selectedTransactions.clear();
        this.loadTransactions();
        this.loadChartData();
      });
    });
  }

  ngOnInit(): void {
    const userIdStr = this.cookieService.get('user_id');
    this.userId = userIdStr ? Number(userIdStr) : 0;

    const today = new Date();
    this.selectedMonthEnd = today.toISOString().slice(0, 7);
    const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    this.selectedMonthStart = prevMonth.toISOString().slice(0, 7);

    this.loadVehicleTypeOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearLiveRefreshInterval();
  }

  // --- Live refresh ---
  onLiveRefreshToggle(): void {
    if (this.enableLiveRefresh) {
      this.startLiveRefreshInterval();
    } else {
      this.clearLiveRefreshInterval();
    }
  }

  private startLiveRefreshInterval(): void {
    this.clearLiveRefreshInterval();
    this.liveRefreshInterval = setInterval(() => {
      this.loadTransactions(true);
      this.loadChartData(true);
    }, 60000);
  }

  private clearLiveRefreshInterval(): void {
    if (this.liveRefreshInterval) {
      clearInterval(this.liveRefreshInterval);
      this.liveRefreshInterval = null;
    }
  }

  get filteredGateOptions(): string[] {
    if (!this.gateSearchText) return this.gateOptions;
    return this.gateOptions.filter(gate => gate.toLowerCase().includes(this.gateSearchText.toLowerCase()));
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get isAllSelected(): boolean {
    const activeTx = this.transactions.filter(t => !t.exitDate);
    if (activeTx.length === 0) return false;
    return activeTx.every(t => this.selectedTransactions.has(t.id));
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.statusFilter !== 'all') count++;
    count += Object.keys(this.selectedGates).length;
    count += Object.keys(this.selectedVehicleTypes).length;
    if (this.searchText) count++;
    return count;
  }

  get activeGateCount(): number {
    return Object.keys(this.selectedGates).length;
  }

  get activeVehicleTypeCount(): number {
    return Object.keys(this.selectedVehicleTypes).length;
  }

  get canEdit(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  openEditLicenseModal(item: ParkingTransactionResponse): void {
    if (!this.canEdit) return;
    console.log("Licese data edit : ", item)
    this.editLicenseData = {
      userId: this.userId,
      oldLicense: item.entryLicense || '',
      transactionId: item.id,
      entryPath1: item.entryPath1 || '',
    };
    this.showEditLicenseModal = true;
  }

  closeEditLicenseModal(): void {
    this.showEditLicenseModal = false;
  }

  onEditLicenseSuccess(): void {
    this.loadTransactions(true);
  }

  openForceEnterModal(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้างรายการเข้า');
      return;
    }
    if (this.selectedSiteId <= 0) {
      this.toast.error('กรุณาเลือกลานจอด');
      return;
    }
    this.showForceEnterModal = true;
  }

  closeForceEnterModal(): void {
    this.showForceEnterModal = false;
  }

  onForceEnterSuccess(): void {
    this.loadTransactions(true);
  }

  openForceExitModal(item: ParkingTransactionResponse): void {
    if (!this.canEdit) {
      this.toast.error('ไม่มีสิทธิ์ทำรายการออก');
      return;
    }
    if (item.exitDate) return;
    if (!(item.cardCode || '').trim()) {
      this.toast.error('รายการนี้ไม่มี card code');
      return;
    }
    this.selectedForceExitTransaction = item;
    this.showForceExitModal = true;
  }

  closeForceExitModal(): void {
    this.showForceExitModal = false;
    this.selectedForceExitTransaction = null;
  }

  onForceExitSuccess(): void {
    this.loadTransactions(true);
  }

  toggleSelection(id: number): void {
    const item = this.transactions.find(t => t.id === id);
    if (!item || item.exitDate) {
      this.selectedTransactions.delete(id);
      return;
    }
    if (this.selectedTransactions.has(id)) {
      this.selectedTransactions.delete(id);
    } else {
      this.selectedTransactions.add(id);
    }
  }

  expandedCardIds = new Set<number>();

  toggleCardExpand(id: number, event: Event): void {
    event.stopPropagation();
    if (this.expandedCardIds.has(id)) {
      this.expandedCardIds.delete(id);
    } else {
      this.expandedCardIds.add(id);
    }
  }

  toggleSelectAll(): void {
    const activeTx = this.transactions.filter(t => !t.exitDate);
    if (this.isAllSelected) {
      this.selectedTransactions.clear();
    } else {
      activeTx.forEach(t => this.selectedTransactions.add(t.id));
    }
  }

  openBulkCloseModal(): void {
    if (this.selectedTransactions.size === 0) return;
    if (!this.canEdit) {
      this.toast.error('ไม่มีสิทธิ์ทำรายการออก');
      return;
    }
    this.bulkRemark = '';
    this.bulkLocalOnly = false;
    this.bulkExitGateId = 0;
    this.bulkGateSearchText = '';
    this.isBulkGateDropdownOpen = false;
    this.loadBulkExitGates();
    this.showBulkCloseModal = true;
  }

  closeBulkCloseModal(): void {
    if (this.isBulkClosing) return;
    this.showBulkCloseModal = false;
    this.bulkRemark = '';
    this.isBulkGateDropdownOpen = false;
  }

  confirmBulkClose(): void {
    const remark = this.bulkRemark.trim();
    if (!remark) {
      this.toast.error('กรุณาระบุเหตุผลในการทำรายการออก');
      return;
    }
    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!menuId) {
      this.toast.error('ไม่พบ Menu ID');
      return;
    }
    if (!this.bulkLocalOnly && this.bulkExitGates.length > 0 && this.bulkExitGateId <= 0) {
      this.toast.error('กรุณาเลือกประตูออก');
      return;
    }

    this.isBulkClosing = true;
    const payload = {
      userId: this.userId,
      remark,
      menuId,
      parkingGateId: this.bulkLocalOnly ? 0 : this.bulkExitGateId,
      localOnly: this.bulkLocalOnly,
    };
    const ids = Array.from(this.selectedTransactions);
    const requests = ids.map(id => this.apiService.closingTransaction(payload, id));

    forkJoin(requests).subscribe({
      next: (results) => {
        this.isBulkClosing = false;
        const failures = results
          .map((r, i) => ({ r, id: ids[i] }))
          .filter(x => !x.r?.isSuccess);
        const successes = ids.length - failures.length;
        if (successes > 0) {
          this.toast.success(`ทำรายการสำเร็จ ${successes}/${ids.length} รายการ`);
        }
        if (failures.length > 0) {
          const plateOf = (id: number) =>
            this.transactions.find(t => t.id === id)?.entryLicense || `#${id}`;
          const detail = failures
            .slice(0, 3)
            .map(x => `${plateOf(x.id)}: ${x.r?.message || 'ไม่สำเร็จ'}`)
            .join(' | ');
          const more = failures.length > 3 ? ` และอีก ${failures.length - 3} รายการ` : '';
          this.toast.error(`ไม่สำเร็จ ${failures.length} รายการ — ${detail}${more}`);
        }
        this.selectedTransactions.clear();
        this.showBulkCloseModal = false;
        this.bulkRemark = '';
        this.loadTransactions(true);
      },
      error: (err) => {
        console.error(err);
        this.isBulkClosing = false;
        this.toast.error('เกิดข้อผิดพลาดในการทำรายการบางส่วน');
        this.loadTransactions(true);
      },
    });
  }

  openImageModal(url: string): void {
    if (url) {
      this.selectedImageUrl = url;
      this.showImageModal = true;
    }
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImageUrl = '';
  }

  openDetailModal(parkingTransaction: ParkingTransactionResponse): void {
    this.selectedParkingTransaction = parkingTransaction;
    this.showDetailModal = true;
  }

  closeDetailModal(): void {
    this.showDetailModal = false;
    this.selectedParkingTransaction = null;
  }

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  toggleMobileFilter(): void {
    this.showMobileFilter = !this.showMobileFilter;
  }

  closeMobileFilter(): void {
    this.showMobileFilter = false;
  }

  toggleFilterSection(section: string): void {
    this.collapsedFilters[section] = !this.collapsedFilters[section];
  }

  isFilterCollapsed(section: string): boolean {
    return !!this.collapsedFilters[section];
  }

  clearAllFilters(): void {
    this.statusFilter = 'all';
    this.selectedGates = {};
    this.gateSearchText = '';
    this.selectedVehicleTypes = {};
    this.searchText = '';
    this.currentPage = 1;
    this.loadTransactions();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.selectedTransactions.clear();
    this.loadTransactions();
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = Number(target.value);
    this.currentPage = 1;
    this.selectedTransactions.clear();
    this.loadTransactions();
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.currentPage = 1;
    this.loadTransactions();
  }

  setStatusFilter(status: 'all' | 'active' | 'completed'): void {
    if (this.statusFilter === status) {
      // Re-click same primary filter still reloads from API.
      this.currentPage = 1;
      this.loadTransactions();
      return;
    }
    this.statusFilter = status;
    this.currentPage = 1;
    this.selectedTransactions.clear();
    this.loadTransactions();
  }

  filterData(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  toggleGate(gateName: string): void {
    if (this.selectedGates[gateName]) {
      delete this.selectedGates[gateName];
    } else {
      this.selectedGates[gateName] = true;
    }
    this.currentPage = 1;
    this.loadTransactions();
  }

  resetGateFilter(): void {
    this.selectedGates = {};
    this.gateSearchText = '';
    this.currentPage = 1;
    this.loadTransactions();
  }

  toggleVehicleType(type: string): void {
    if (this.selectedVehicleTypes[type]) {
      delete this.selectedVehicleTypes[type];
    } else {
      this.selectedVehicleTypes[type] = true;
    }
    this.currentPage = 1;
    this.loadTransactions();
  }

  resetVehicleTypeFilter(): void {
    this.selectedVehicleTypes = {};
    this.currentPage = 1;
    this.loadTransactions();
  }

  onDateChange(): void {
    this.currentPage = 1;
    this.loadTransactions();
    if (this.chartMode === 'daily' && this.dailySubMode === 'default') {
      this.loadChartData();
    }
  }

  onRefresh(): void {
    this.searchText = '';
    this.selectedGates = {};
    this.gateSearchText = '';
    // Keep statusFilter + selectedVehicleTypes (master) — refresh ต้องไม่เคลียร์การเลือก
    this.currentPage = 1;
    this.selectedTransactions.clear();
    this.loadTransactions();
    this.loadChartData();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'license_plate' | 'site_name' | 'card_name' | 'gate_name'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.filterData();
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'license_plate': return 'ทะเบียนรถ';
      case 'card_name': return 'บัตร/สมาชิก';
      case 'gate_name': return 'จุดเข้า-ออก';
      default: return 'ค้นหา';
    }
  }

  formatDateDisplay(dateStr: string | null): string {
    if (!dateStr) return '-';
    if (dateStr.startsWith('0001')) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hour = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hour}:${min}`;
  }

  calculateDuration(entry: string | null, exit: string | null): string {
    if (!entry || !exit) return '-';
    if (entry.startsWith('0001') || exit.startsWith('0001')) return '-';
    const start = new Date(entry).getTime();
    const end = new Date(exit).getTime();
    const diffMs = end - start;
    if (diffMs < 0) return '-';
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (diffHrs > 24) {
      const days = Math.floor(diffHrs / 24);
      const remainHrs = diffHrs % 24;
      return `${days} วัน ${remainHrs} ชม. ${diffMins} นาที`;
    }
    return `${diffHrs} ชม. ${diffMins} นาที`;
  }

  getVehicleIcon(vehicleType: string | null): string {
    if (!vehicleType) return 'directions_car';
    const type = vehicleType.toLowerCase();
    if (type.includes('motor') || type.includes('bike') || type.includes('จักรยาน') || type.includes('มอเตอร์')) {
      return 'two_wheeler';
    }
    return 'directions_car';
  }

  // --- API ---
  /** Master ประเภทรถสำหรับ filter sidebar — ส่งชื่อที่เลือกเป็น vehicleTypeNames ไปตาราง */
  private loadVehicleTypeOptions(): void {
    this.apiFormService.getParkingVehicleType().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.vehicleTypeOptions = (res ?? [])
          .map(item => ({
            id: Number(item.id) || 0,
            name: (item.typeName || '').trim(),
          }))
          .filter(x => !!x.name)
          .sort((a, b) => a.id - b.id)
          .map(x => x.name);
        this.sanitizeSelectedVehicleTypes();
      },
      error: (err) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดรายการประเภทรถได้');
      },
    });
  }

  loadTransactions(isSilent = false): void {
    const siteId = this.siteState.selectedSite()?.id;
    if (!siteId || !this.userId) return;

    if (!isSilent) this.isLoading = true;

    const searchField = this.searchText.trim()
      ? this.mapper.mapSearchField(this.searchType)
      : undefined;

    this.apiService.getParkingTransactionForTable({
      siteId,
      userId: this.userId,
      startDate: this.startDate,
      endDate: this.endDate,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField,
      searchText: this.searchText.trim() || undefined,
      sortColumn: this.mapper.mapSortColumn(this.sortColumn),
      sortDirection: this.sortDirection,
      status: this.statusFilter,
      gateNames: this.mapper.mapSelectedNames(this.selectedGates),
      vehicleTypeNames: this.mapper.mapSelectedNames(this.selectedVehicleTypes)
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const mapped = this.mapper.mapTableFromDto(res);
        this.transactions = mapped.items;
        this.pruneExitedSelections();
        this.totalItems = mapped.total;
        this.totalPages = mapped.totalPages || 1;

        if (mapped.summary) {
          this.dashboardStats = mapped.summary;
        }

        if (mapped.gateOptions.length) {
          this.gateOptions = mapped.gateOptions;
          this.sanitizeSelectedGates();
        }

        // ประเภทรถใช้ master จาก GetParkingVehicleType — ไม่ทับด้วย options จากตาราง
        // fallback เฉพาะเมื่อยังโหลด master ไม่สำเร็จ
        if (!this.vehicleTypeOptions.length && mapped.vehicleTypeOptions.length) {
          this.vehicleTypeOptions = mapped.vehicleTypeOptions;
          this.sanitizeSelectedVehicleTypes();
        }

        this.lastFetchedTime = new Date();
        if (!isSilent) this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        if (!isSilent) {
          this.toast.error('ไม่สามารถโหลดข้อมูล Parking Transaction ได้');
          this.isLoading = false;
        }
      },
    });
  }

  private sanitizeSelectedGates(): void {
    Object.keys(this.selectedGates).forEach(gate => {
      if (!this.gateOptions.includes(gate)) {
        delete this.selectedGates[gate];
      }
    });
  }

  private sanitizeSelectedVehicleTypes(): void {
    Object.keys(this.selectedVehicleTypes).forEach(type => {
      if (!this.vehicleTypeOptions.includes(type)) {
        delete this.selectedVehicleTypes[type];
      }
    });
  }

  loadChartData(isSilent = false): void {
    const siteId = this.siteState.selectedSite()?.id;
    if (!siteId || !this.userId) return;

    let start = this.startDate;
    let end = this.endDate;

    if (this.chartMode === 'daily') {
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = this.customStartDate;
        end = this.customEndDate;
      } else {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setDate(today.getDate() - 6);
        start = this.formatDate(pastDate);
        end = this.formatDate(today);
      }
    } else {
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      if (startYear && startMonth && endYear && endMonth) {
        start = this.formatDate(new Date(startYear, startMonth - 1, 1));
        end = this.formatDate(new Date(endYear, endMonth, 0));
      }
    }

    this.chartStartDate = start;
    this.chartEndDate = end;

    this.apiService.getParkingTransactionChart({
      siteId,
      userId: this.userId,
      startDate: start,
      endDate: end,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.dashboardData = this.mapper.mapChartItems(res);
        this.processDashboardData();
      },
      error: (err) => {
        if (!isSilent) console.error('Error fetching chart data:', err);
      },
    });
  }

  formatDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  setChartMode(mode: 'daily' | 'monthly'): void {
    this.chartMode = mode;
    if (mode === 'monthly') {
      const today = new Date();
      this.selectedMonthEnd = today.toISOString().slice(0, 7);
      this.selectedMonthStart = today.toISOString().slice(0, 7);
      this.onChartMonthChange();
      return;
    }
    this.loadChartData();
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
  }

  onChartMonthChange(): void {
    if (this.chartMode !== 'monthly') return;
    this.loadChartData();
  }

  setDailySubMode(mode: 'default' | 'custom'): void {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) this.customStartDate = this.startDate;
      if (!this.customEndDate) {
        const s = new Date(this.customStartDate);
        const e = new Date(this.customStartDate);
        e.setDate(s.getDate() + 6);
        this.customEndDate = this.formatDate(e);
      }
      this.onCustomDateChange();
    } else {
      this.loadChartData();
    }
  }

  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 10);
    return this.formatDate(d);
  }

  resetCustomDate(): void {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customEndDate = this.formatDate(e);
    this.customStartDate = this.formatDate(s);
    this.loadChartData();
  }

  onCustomDateChange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;
    const diffDays = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      this.toast.warn('แสดงผลได้สูงสุด 10 วัน');
      const newEnd = new Date(s);
      newEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDate(newEnd);
    }
    this.loadChartData();
  }

  processDashboardData(): void {
    let xLabels: Date[] = [];
    let dataPoints: number[] = [];
    let maxVal = 5;

    if (this.chartMode === 'daily') {
      let start: Date;
      let end: Date;
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = new Date(this.customStartDate);
        end = new Date(this.customEndDate);
      } else {
        start = new Date(this.chartStartDate);
        end = new Date(this.chartEndDate);
      }
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        xLabels.push(new Date(d));
        dataPoints.push(0);
      }
      this.dashboardData.forEach(m => {
        if (!m.entryDate) return;
        const d = new Date(m.entryDate);
        if (isNaN(d.getTime())) return;
        const dDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        if (dDateOnly >= startDateOnly && dDateOnly <= endDateOnly) {
          const idx = xLabels.findIndex(x => x.toDateString() === dDateOnly.toDateString());
          if (idx !== -1) dataPoints[idx]++;
        }
      });
    } else {
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth, 0);
      const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          xLabels.push(new Date(d));
          dataPoints.push(0);
        }
        this.dashboardData.forEach(m => {
          if (!m.entryDate) return;
          const d = new Date(m.entryDate);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex(x => x.toDateString() === d.toDateString());
            if (idx !== -1) dataPoints[idx]++;
          }
        });
      } else {
        let temp = new Date(startYear, startMonth - 1, 1);
        while (temp <= end) {
          const year = temp.getFullYear();
          const month = temp.getMonth();
          [1, 6, 11, 16, 21, 26].forEach(day => {
            const chunkDate = new Date(year, month, day);
            if (chunkDate <= end && chunkDate >= start) {
              xLabels.push(chunkDate);
              dataPoints.push(0);
            }
          });
          temp.setMonth(temp.getMonth() + 1);
        }
        this.dashboardData.forEach(m => {
          if (!m.entryDate) return;
          const d = new Date(m.entryDate);
          if (d < start || d > end) return;
          let chunkStart = 1;
          const date = d.getDate();
          if (date >= 26) chunkStart = 26;
          else if (date >= 21) chunkStart = 21;
          else if (date >= 16) chunkStart = 16;
          else if (date >= 11) chunkStart = 11;
          else if (date >= 6) chunkStart = 6;
          const idx = xLabels.findIndex(x =>
            x.getFullYear() === d.getFullYear() &&
            x.getMonth() === d.getMonth() &&
            x.getDate() === chunkStart
          );
          if (idx !== -1) dataPoints[idx]++;
        });
      }
    }

    this.chartTotalCount = dataPoints.reduce((a, b) => a + b, 0);
    const rawMax = Math.max(...dataPoints, 5);
    maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - ((val / maxVal) * 100) };
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - ((val / maxVal) * 100);
      return { x, y };
    };

    const getThaiMonth = (monthIndex: number) =>
      ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'][monthIndex];

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map(p => `${p.x},${p.y}`).join(' ');

    const [sY, sM] = this.selectedMonthStart.split('-').map(Number);
    const [eY, eM] = this.selectedMonthEnd.split('-').map(Number);
    const mStart = new Date(sY, sM - 1, 1);
    const mEnd = new Date(eY, eM, 0);
    const mDiff = Math.ceil(Math.abs(mEnd.getTime() - mStart.getTime()) / (1000 * 60 * 60 * 24));
    const isDailyGranularity = this.chartMode === 'daily' || (this.chartMode === 'monthly' && mDiff <= 60);

    this.currentMonthDots = pointsObj.map((p, i) => {
      const date = xLabels[i];
      let label = '';
      let subLabel = '';
      if (isDailyGranularity) {
        label = `${date.getDate()}`;
        if (date.getDate() === 1 || i === 0) {
          subLabel = getThaiMonth(date.getMonth());
        }
      } else {
        const day = date.getDate();
        label = day === 26
          ? `26-${new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()}`
          : `${day}-${day + 4}`;
        if (day === 1) subLabel = `${getThaiMonth(date.getMonth())} ${date.getFullYear()}`;
      }
      return { x: p.x, y: p.y, label, subLabel, value: dataPoints[i], color: this.getChartColor(i) };
    });

    if (pointsObj.length > 0) {
      const firstPoint = pointsObj[0];
      const lastPoint = pointsObj[pointsObj.length - 1];
      this.currentMonthAreaPoints = `${firstPoint.x},100 ${this.currentMonthPoints} ${lastPoint.x},100`;
    } else {
      this.currentMonthAreaPoints = '';
    }

    const sortedByDate = [...this.dashboardData].sort((a, b) => {
      const dateA = a.entryDate ? new Date(a.entryDate).getTime() : 0;
      const dateB = b.entryDate ? new Date(b.entryDate).getTime() : 0;
      return dateB - dateA;
    });

    this.recentActivity = sortedByDate.slice(0, 5).map(m => ({
      initial: m.entryLicense ? m.entryLicense.charAt(0).toUpperCase() : '?',
      name: m.entryLicense,
      timeAgo: this.getTimeAgo(m.entryDate),
      info: m.vehicleTypeName || '',
      color: this.getRandomColor(m.id || 0),
    }));
  }

  getTimeAgo(dateStr: string | Date | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay > 0) return `${diffDay} วันที่แล้ว`;
    if (diffHr > 0) return `${diffHr} ชม. ที่แล้ว`;
    if (diffMin > 0) return `${diffMin} นาทีที่แล้ว`;
    return 'เมื่อกี้';
  }

  getRandomColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }

  getChartColor(index: number): string {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }

  // --- Bulk close: exit gate (ลาน Taiwan ต้องใช้ส่ง force_exit_site) ---
  bulkExitGates: ParkingGateListResponse[] = [];
  bulkExitGateId = 0;
  bulkGateSearchText = '';
  isBulkGateDropdownOpen = false;

  get filteredBulkExitGates(): ParkingGateListResponse[] {
    const q = this.bulkGateSearchText.trim().toLowerCase();
    const selectedName = this.selectedBulkExitGateName.toLowerCase();
    if (!q || q === selectedName) return this.bulkExitGates;
    return this.bulkExitGates.filter(g => g.name.toLowerCase().includes(q));
  }

  get selectedBulkExitGateName(): string {
    return this.bulkExitGates.find(g => g.id === this.bulkExitGateId)?.name ?? '';
  }

  selectBulkExitGate(gate: ParkingGateListResponse): void {
    this.bulkExitGateId = gate.id;
    this.bulkGateSearchText = gate.name;
    this.isBulkGateDropdownOpen = false;
  }

  closeBulkGateDropdown(): void {
    this.isBulkGateDropdownOpen = false;
    this.bulkGateSearchText = this.selectedBulkExitGateName;
  }

  private loadBulkExitGates(): void {
    this.bulkExitGates = [];
    if (this.selectedSiteId <= 0) return;
    this.legacyApiService.getParkingGate(this.selectedSiteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.bulkExitGates = (res ?? []).filter(g => {
          const raw = g as ParkingGateListResponse & { Exit?: boolean };
          return g.exit === true || raw.Exit === true;
        });
        if (this.bulkExitGates.length === 1) {
          this.selectBulkExitGate(this.bulkExitGates[0]);
        }
      },
      error: () => (this.bulkExitGates = []),
    });
  }

  /** ปิดใน DB อย่างเดียว ไม่ส่ง Taiwan — กรณี Taiwan ปิดไปแล้วแต่ไม่ได้แจ้งกลับมา */
  bulkLocalOnly = false;

  get hasActiveTransactions(): boolean {
    return this.transactions.some(t => !t.exitDate);
  }

  private pruneExitedSelections(): void {
    if (this.selectedTransactions.size === 0) return;
    const activeIds = new Set(this.transactions.filter(t => !t.exitDate).map(t => t.id));
    this.selectedTransactions.forEach(id => {
      if (!activeIds.has(id)) this.selectedTransactions.delete(id);
    });
  }
}
